import { LoadingScreen, NotificationFeed } from '@sparklayer-io/dashboard';
import { Outlet, useMatches } from '@tanstack/react-router';
import { type ComponentType, lazy, Suspense } from 'react';
import { Layout } from '../components/layout';

export const DEFAULT_TITLE = 'SparkLayer Dashboard';

export function PendingScreen() {
  return <LoadingScreen />;
}

export function HostError({ error }: { error: Error }) {
  return <LoadingScreen message={error.message} />;
}

export function RootLayout() {
  return (
    <>
      <Outlet />
      <NotificationFeed />
    </>
  );
}

/**
 * The authenticated site chrome - sidebar, alert bars, mobile header - ported in
 * `../components/layout`. The matched leaf's `staticData` picks the layout shape, so a full-bleed
 * page (the forms app, the theme editor) asks for it the way it asks for its title.
 */
export function SiteShell() {
  const matches = useMatches();
  const { fullBleed, fullHeight } = matches[matches.length - 1]?.staticData ?? {};
  return <Layout fullBleed={fullBleed} fullHeight={fullHeight} />;
}

/**
 * Route `component` that lazily resolves the `name` export from a page barrel and renders it inside a
 * `<Suspense>` with the shared pending screen. Shared by every `routes/<family>` module so the page
 * packages stay out of the router/guard graph until the route renders.
 */
export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
  return function LazyPage() {
    return (
      <Suspense fallback={<PendingScreen />}>
        <Lazy />
      </Suspense>
    );
  };
}

export const HomeIndexRoute = lazyPage(() => import('./site/home-index'), 'HomeIndexHost');

export const PartnerOverviewRoute = lazyPage(
  () => import('./site/partner-overview'),
  'PartnerOverviewHost',
);

export const SettingsPasswordRoute = lazyPage(
  () => import('./settings/password'),
  'SettingsPasswordHost',
);

export const GuestLoginRoute = lazyPage(() => import('./auth/guest-login'), 'GuestLoginHost');

export const RegisterRoute = lazyPage(() => import('./auth/register'), 'RegisterHost');

export const LogoutRoute = lazyPage(() => import('./auth/logout'), 'LogoutHost');

export const NoTeamRoute = lazyPage(() => import('./auth/no-team'), 'NoTeamHost');

export const TwoFactorChallengeRoute = lazyPage(
  () => import('./auth/two-factor-challenge'),
  'TwoFactorChallengeHost',
);

// Auth / standalone pages all live in the dashboard package and share one lazily loaded chunk.
export const PartnerRegisterRoute = lazyPage(() => import('./auth/hosts'), 'PartnerRegisterHost');
export const EnterpriseRegisterRoute = lazyPage(
  () => import('./auth/hosts'),
  'EnterpriseRegisterHost',
);
export const TeamMemberRegisterRoute = lazyPage(
  () => import('./auth/hosts'),
  'RegisterTeamMemberHost',
);
export const RegisterInviteRoute = lazyPage(() => import('./auth/hosts'), 'RegisterInviteHost');
export const ForgottenPasswordRoute = lazyPage(
  () => import('./auth/hosts'),
  'ForgottenPasswordHost',
);
export const ResetPasswordRoute = lazyPage(() => import('./auth/hosts'), 'ResetPasswordHost');
export const SwitchAccountRoute = lazyPage(() => import('./auth/hosts'), 'SwitchAccountHost');
export const GettingReadyRoute = lazyPage(() => import('./auth/hosts'), 'GettingReadyHost');
